import Matter from 'matter-js';
import { clearsWalls, moveInsideWalls } from './wallContainment';
import type { BubbleDef, Student, MapDefinition, GameConfig, MarbleResult, GameResult } from '../types';
import { getMapById } from './maps';
import { shuffle } from '../utils/helpers';
import { GameAudio } from '../audio/GameAudio';

const FIXED_DELTA = 1000 / 60;
const STUCK_THRESHOLD = 1.2;
const STUCK_DELAY = 1000;
const FORCE_FINISH_MS = 60000;
const MAX_SPEED = 4.5;
const FIRST_GOAL_DRAMA_MS = 2800;
// Random final-approach obstacles placed above the baked finish zone (rotors at y≈3860-3900)
// finalY = poolY - 150, so pool y ≤ 3810 → finalY ≤ 3660, safely above finish zone
const FINAL_OBSTACLE_POOL = [
  { x: 280, y: 3560, w: 110, s: 1.5 },
  { x: 210, y: 3620, w: 95, s: -1.8 },
  { x: 350, y: 3620, w: 95, s: 1.8 },
  { x: 280, y: 3680, w: 85, s: -2.0 },
  { x: 280, y: 3720, w: 75, s: 2.2 },
  { x: 242, y: 3760, w: 64, s: 1.55 },
  { x: 318, y: 3760, w: 64, s: -1.55 },
  { x: 280, y: 3810, w: 58, s: 1.35 },
];
const FINAL_POPPER_POOL = [
  { x: 280, y: 3530 },
  { x: 235, y: 3585 },
  { x: 325, y: 3585 },
  { x: 190, y: 3645 },
  { x: 370, y: 3645 },
  { x: 280, y: 3700 },
  { x: 242, y: 3780 },
  { x: 318, y: 3780 },
  { x: 280, y: 3810 },
];
const FINAL_BUBBLE_POOL = [
  { x: 228, y: 3680 },
  { x: 332, y: 3680 },
  { x: 250, y: 3740 },
  { x: 310, y: 3740 },
  { x: 280, y: 3810 },
];
const FINAL_ITEM_POOL = [
  { kind: 'boost', x: 230, y: 3600 },
  { kind: 'slow', x: 330, y: 3600 },
  { kind: 'vortex', x: 280, y: 3680 },
  { kind: 'anchor', x: 242, y: 3780 },
  { kind: 'teleport', x: 318, y: 3780 },
  { kind: 'swap', x: 280, y: 3820 },
] as const;

interface BubbleState {
  def: BubbleDef;
  body: Matter.Body | null;
  baseX: number;
  baseY: number;
  seed: number;
  burstTime: number;
  burstX: number;
  burstY: number;
}

interface PegBodyState {
  def: BubbleDef;
  body: Matter.Body;
  baseX: number;
  baseY: number;
  seed: number;
}

interface MarbleData {
  body: Matter.Body;
  student: Student;
  color: string;
  hue: number;
  finished: boolean;
  rank: number;
  finishTime: number;
  opacity: number;
  stuckTimer: number;
  progressTimer: number;
  lastProgressY: number;
  lastPos: { x: number; y: number };
  bubbleBounceTime: number;
  spin: number;
  usedItems: Set<string>;
  usedPoppers: Set<string>;
  trail: Array<{ x: number; y: number; a: number }>;
  pulseT: number;
  shielded: boolean;
}

interface EffectToast {
  text: string;
  x: number;
  y: number;
  color: string;
  start: number;
}

export type GamePhase = 'idle' | 'countdown' | 'running' | 'finished';

export interface GameCallbacks {
  onGoal?: (rank: number, name: string, color: string) => void;
  onResultReady?: (result: GameResult) => void;
  onFinish?: (result: GameResult) => void;
  onCountdown?: (n: number) => void;
  onPhaseChange?: (phase: GamePhase) => void;
}

export class GameEngine {
  private engine: Matter.Engine;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;

  private marbles: MarbleData[] = [];
  private finishedMarbles: MarbleData[] = [];
  private rankCounter = 0;
  private resultReadyEmitted = false;
  private mapDef: MapDefinition;
  private config: GameConfig;
  private rotorBodies: Matter.Body[] = [];
  private containmentWalls: MapDefinition['walls'] = [];
  private wallBodyList: Matter.Body[] = [];
  private pegBodies: PegBodyState[] = [];
  private bubbleStates: Map<string, BubbleState> = new Map();
  private consumedItems: Set<string> = new Set();
  private usedPopperIds: Set<string> = new Set();
  private effectToasts: EffectToast[] = [];

  private phase: GamePhase = 'idle';
  private isRunning = false;
  private countdownValue = 3;
  private countdownTimer = 0;
  private gameTime = 0;

  private rafId = 0;
  private lastTime = 0;
  private accumulator = 0;

  private tensionScale = 1.0;
  private firstGoalTime = -1;

  private scale = 1;
  private offsetX = 0;
  private viewportH = 640;
  private cameraY = 0;
  private cameraZoom = 1;
  private targetCameraZoom = 1;
  private hoverCameraY: number | null = null;
  private resizeObserver: ResizeObserver;

  private audio: GameAudio | null = null;

  callbacks: GameCallbacks = {};

  constructor(canvas: HTMLCanvasElement, config: GameConfig) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.config = config;
    this.mapDef = this.createRunMap(getMapById(config.mapId));

    this.engine = Matter.Engine.create({ gravity: { x: 0, y: 1.02 } });
    this.engine.positionIterations = 16;
    this.engine.velocityIterations = 8;
    this.engine.constraintIterations = 4;
    Matter.Events.on(this.engine, 'beforeUpdate', () => { this.limitMarbleSpeed(); });
    const capRotorMarbleVelocity = (event: Matter.IEventCollision<Matter.Engine>) => {
      for (const pair of event.pairs) {
        const isMarble = (b: Matter.Body) => b.label === 'marble';
        const isRotor  = (b: Matter.Body) => b.label === 'rotor';
        if (!((isMarble(pair.bodyA) || isMarble(pair.bodyB)) && (isRotor(pair.bodyA) || isRotor(pair.bodyB)))) continue;
        const marble = isMarble(pair.bodyA) ? pair.bodyA : pair.bodyB;
        const speed = Math.hypot(marble.velocity.x, marble.velocity.y);
        if (speed > MAX_SPEED) {
          const scale = MAX_SPEED / speed;
          Matter.Body.setVelocity(marble, { x: marble.velocity.x * scale, y: marble.velocity.y * scale });
        }
      }
    };

    Matter.Events.on(this.engine, 'collisionStart',  capRotorMarbleVelocity);
    Matter.Events.on(this.engine, 'collisionActive', capRotorMarbleVelocity);

    Matter.Events.on(this.engine, 'collisionStart', (event) => {
      for (const pair of event.pairs) {
        const isMarble = (b: Matter.Body) => b.label === 'marble';
        const isObstacle = (b: Matter.Body) => b.label === 'peg' || b.label === 'rotor' || b.label === 'wall';
        if ((isMarble(pair.bodyA) && isObstacle(pair.bodyB)) || (isMarble(pair.bodyB) && isObstacle(pair.bodyA))) {
          const marble = isMarble(pair.bodyA) ? pair.bodyA : pair.bodyB;
          const speed = Math.hypot(marble.velocity.x, marble.velocity.y);
          this.audio?.playCollision(speed / MAX_SPEED);
          break;
        }
      }
    });

    this.setupCanvas();
    this.buildMap();
    this.buildMarbles();

    this.resizeObserver = new ResizeObserver(() => this.setupCanvas());
    this.resizeObserver.observe(canvas);

    this.rafId = requestAnimationFrame(this.loop);
  }

  private createRunMap(base: MapDefinition): MapDefinition {
    const stretched = this.stretchMap(base);
    const isCustom = base.id.startsWith('custom-');
    const shouldApplyRandomFinal = this.config.useRandomFinalObstacles ?? !isCustom;

    if (!shouldApplyRandomFinal) {
      return {
        ...stretched,
        walls: [...stretched.walls],
        pegs: [...stretched.pegs],
        rotors: [...(stretched.rotors ?? [])],
        boostZones: [...(stretched.boostZones ?? [])],
        itemZones: this.config.itemsEnabled ? [...(stretched.itemZones ?? [])] : [],
        poppers: [...(stretched.poppers ?? [])],
        bubbles: [...(stretched.bubbles ?? [])],
      };
    }

    const finalRotors = shuffle(FINAL_OBSTACLE_POOL)
      .slice(0, 2 + Math.floor(Math.random() * 2))
      .map((r, i) => ({
        x: r.x,
        y: this.finalY(stretched, r.y),
        width: r.w,
        height: 9,
        angularVelocity: r.s * (0.85 + Math.random() * 0.55),
        restitution: 0.22,
        angle: (Math.random() - 0.5) * 0.4 + i * 0.05,
      }));
    const finalPoppers = shuffle(FINAL_POPPER_POOL)
      .slice(0, 2 + Math.floor(Math.random() * 2))
      .map((p, i) => ({
        id: `run-final-pop-${Date.now()}-${i}`,
        x: p.x + (Math.random() - 0.5) * 18,
        y: this.finalY(stretched, p.y) + (Math.random() - 0.5) * 22,
        radius: 19,
        force: 5.8 + Math.random() * 1.0,
      }));
    const finalBubbles = shuffle(FINAL_BUBBLE_POOL)
      .slice(0, 1 + Math.floor(Math.random() * 2))
      .map((b, i) => ({
        id: `run-final-bubble-${Date.now()}-${i}`,
        x: b.x + (Math.random() - 0.5) * 16,
        y: this.finalY(stretched, b.y) + (Math.random() - 0.5) * 20,
        radius: 13 + Math.random() * 4,
        restitution: 0.92,
      }));
    const finalItems = this.config.itemsEnabled
      ? shuffle([...FINAL_ITEM_POOL])
        .slice(0, 1 + Math.floor(Math.random() * 2))
        .map((item, i) => ({
          id: `run-final-item-${Date.now()}-${i}`,
          kind: item.kind,
          x: item.x + (Math.random() - 0.5) * 20,
          y: this.finalY(stretched, item.y) + (Math.random() - 0.5) * 22,
          radius: 23,
        }))
      : [];

    return {
      ...stretched,
      walls: [...stretched.walls],
      pegs: [...stretched.pegs],
      rotors: [...(stretched.rotors ?? []), ...finalRotors],
      boostZones: [...(stretched.boostZones ?? [])],
      itemZones: this.config.itemsEnabled ? [...(stretched.itemZones ?? []), ...finalItems] : [],
      poppers: [...(stretched.poppers ?? []), ...finalPoppers],
      bubbles: [...(stretched.bubbles ?? []), ...finalBubbles],
    };
  }

  private stretchMap(base: MapDefinition): MapDefinition {
    const factor = this.config.courseLength === 'long' ? 2 : this.config.courseLength === 'medium' ? 1.5 : 1;
    if (factor === 1) return JSON.parse(JSON.stringify(base)) as MapDefinition;
    const anchor = base.startZoneY;
    const sy = (y: number) => anchor + (y - anchor) * factor;
    const stretchWall = (wall: typeof base.walls[number]) => {
      const angle = wall.angle ?? 0;
      const half = wall.width / 2;
      const x1 = wall.x - Math.cos(angle) * half;
      const y1 = wall.y - Math.sin(angle) * half;
      const x2 = wall.x + Math.cos(angle) * half;
      const y2 = wall.y + Math.sin(angle) * half;
      const ny1 = sy(y1);
      const ny2 = sy(y2);
      const dx = x2 - x1;
      const dy = ny2 - ny1;
      return {
        ...wall,
        x: (x1 + x2) / 2,
        y: (ny1 + ny2) / 2,
        width: Math.hypot(dx, dy),
        angle: Math.atan2(dy, dx),
      };
    };
    const scaleY = <T extends { y: number; height?: number }>(obj: T): T => ({
      ...obj,
      y: sy(obj.y),
    });
    return {
      ...JSON.parse(JSON.stringify(base)),
      worldHeight: Math.round(sy(base.worldHeight)),
      goalY: Math.round(sy(base.goalY)),
      walls: base.walls.map(stretchWall),
      pegs: base.pegs.map((p) => ({ ...p, y: sy(p.y) })),
      rotors: base.rotors?.map(scaleY),
      boostZones: base.boostZones?.map(scaleY),
      itemZones: base.itemZones?.map((i) => ({ ...i, y: sy(i.y) })),
      poppers: base.poppers?.map((p) => ({ ...p, y: sy(p.y) })),
      bubbles: base.bubbles?.map((b) => ({ ...b, y: sy(b.y) })),
    };
  }

  private finalY(map: MapDefinition, baseY: number) {
    const referenceGoal = this.config.courseLength === 'long'
      ? 35 + (4160 - 35) * 2 + 150
      : this.config.courseLength === 'medium'
        ? 35 + (4160 - 35) * 1.5 + 150
        : 4160 + 150;
    return map.goalY - (referenceGoal - baseY);
  }

  private setupCanvas() {
    this.dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    this.canvas.width = rect.width * this.dpr;
    this.canvas.height = rect.height * this.dpr;
    this.ctx.resetTransform();
    this.ctx.scale(this.dpr, this.dpr);
    this.viewportH = Math.min(900, this.mapDef.worldHeight);
    this.scale = Math.min(rect.width / this.mapDef.worldWidth, rect.height / this.viewportH);
    this.updateDrawOffset(rect.width);
  }

  private buildMap() {
    const { walls, pegs, rotors = [] } = this.mapDef;
    const bodies: Matter.Body[] = [];

    const wallOpts: Matter.IBodyDefinition = {
      isStatic: true, label: 'wall', friction: 0.01, restitution: 0.4,
    };
    const sideBoundaryOpts: Matter.IBodyDefinition = { isStatic: true, label: 'wall', friction: 0, restitution: 0.3 };
    this.containmentWalls = [
      ...walls,
      { x: -30, y: this.mapDef.worldHeight / 2, width: 60, height: this.mapDef.worldHeight + 400 },
      { x: this.mapDef.worldWidth + 30, y: this.mapDef.worldHeight / 2, width: 60, height: this.mapDef.worldHeight + 400 },
      { x: this.mapDef.worldWidth / 2, y: -100, width: this.mapDef.worldWidth + 120, height: 20 },
      { x: this.mapDef.worldWidth / 2, y: this.mapDef.worldHeight + 10, width: this.mapDef.worldWidth + 120, height: 20 },
    ];
    // boundary walls
    bodies.push(
      Matter.Bodies.rectangle(this.mapDef.worldWidth / 2, -100, this.mapDef.worldWidth, 20, wallOpts),
      Matter.Bodies.rectangle(this.mapDef.worldWidth / 2, this.mapDef.worldHeight + 10, this.mapDef.worldWidth, 20, wallOpts),
      Matter.Bodies.rectangle(-30, this.mapDef.worldHeight / 2, 60, this.mapDef.worldHeight, sideBoundaryOpts),
      Matter.Bodies.rectangle(this.mapDef.worldWidth + 30, this.mapDef.worldHeight / 2, 60, this.mapDef.worldHeight, sideBoundaryOpts),
    );

    const internalWallBodies: Matter.Body[] = [];
    for (const w of walls) {
      const b = Matter.Bodies.rectangle(w.x, w.y, w.width, w.height, {
        ...wallOpts,
        friction: w.friction ?? wallOpts.friction,
        restitution: w.restitution ?? wallOpts.restitution,
      });
      if (w.angle) Matter.Body.setAngle(b, w.angle);
      bodies.push(b);
      internalWallBodies.push(b);
    }

    for (const p of pegs) {
      const body = Matter.Bodies.circle(p.x, p.y, p.radius, {
        isStatic: true, label: 'peg', friction: 0.03,
        restitution: p.restitution ?? 0.42,
      });
      this.pegBodies.push({
        def: { id: `peg-${p.x}-${p.y}`, x: p.x, y: p.y, radius: p.radius, restitution: p.restitution },
        body,
        baseX: p.x,
        baseY: p.y,
        seed: p.x * 1.31 + p.y * 0.73,
      });
      bodies.push(body);
    }

    for (const r of rotors) {
      const body = Matter.Bodies.rectangle(r.x, r.y, r.width, r.height, {
        isStatic: true,
        label: 'rotor',
        friction: 0.03,
        restitution: r.restitution ?? 0.28,
      });
      Matter.Body.setAngle(body, r.angle ?? 0);
      Matter.Body.setAngularVelocity(body, r.angularVelocity);
      this.rotorBodies.push(body);
      bodies.push(body);
    }

    Matter.Composite.add(this.engine.world, bodies);
    this.wallBodyList = internalWallBodies;

    // Bubble bodies (high-restitution static circles that pop on contact)
    for (const bub of this.mapDef.bubbles ?? []) {
      const body = Matter.Bodies.circle(bub.x, bub.y, bub.radius * 1.25, {
        isStatic: true,
        label: 'bubble',
        restitution: bub.restitution ?? 0.96,
        friction: 0.0,
      });
      Matter.Composite.add(this.engine.world, body);
      this.bubbleStates.set(bub.id, {
        def: bub,
        body,
        baseX: bub.x,
        baseY: bub.y,
        seed: bub.x * 1.11 + bub.y * 0.79,
        burstTime: -1,
        burstX: bub.x,
        burstY: bub.y,
      });
    }
  }

  private buildMarbles() {
    const students = this.config.mode === 'reward'
      ? this.config.students
      : this.config.students;
    const n = students.length;
    const radius = this.marbleRadius(n);

    students.forEach((student, i) => {
      const hue = (360 / Math.max(n, 1)) * i;
      const color = `hsl(${hue}, 75%, 62%)`;
      const pos = this.startPos(i, n, radius);
      // Some maps have inset entrance walls; never spawn on their outer side.
      const candidates = Array.from({ length: Math.floor(this.mapDef.worldWidth / 2) }, (_, j) => radius + 1 + j * 2)
        .filter(x => x < this.mapDef.worldWidth - radius && clearsWalls({ x, y: pos.y }, radius, this.containmentWalls))
        .sort((a, b) => Math.abs(a - pos.x) - Math.abs(b - pos.x));
      if (!candidates.length) throw new Error('출발 구역에 공을 놓을 자리가 없어요.');
      pos.x = candidates[0];

      const body = Matter.Bodies.circle(pos.x, pos.y, radius, {
        label: 'marble',
        restitution: 0.25,
        friction: 0.005,
        frictionAir: 0.004,
        density: 0.0016 + Math.random() * 0.0005,
      });

      Matter.Composite.add(this.engine.world, body);
      this.marbles.push({
        body, student, color, hue,
        finished: false, rank: 0, finishTime: 0, opacity: 1,
        stuckTimer: 0, progressTimer: 0, lastProgressY: pos.y, lastPos: { x: pos.x, y: pos.y },
        spin: 0, usedItems: new Set(), usedPoppers: new Set(),
        trail: [], pulseT: 0, shielded: false, bubbleBounceTime: -9999,
      });
    });
  }

  private marbleRadius(n: number): number {
    if (n <= 5) return 20;
    if (n <= 12) return 19;
    return 18;
  }

  private startPos(index: number, total: number, radius: number) {
    const spacing = radius * 2 + 5;
    const sidePadding = 28;
    const maxCols = Math.max(1, Math.floor((this.mapDef.worldWidth - sidePadding * 2) / spacing) + 1);
    const cols = Math.min(maxCols, total);
    const col = index % cols;
    const row = Math.floor(index / cols);
    const totalW = (Math.min(cols, total) - 1) * spacing;
    const startX = (this.mapDef.worldWidth - totalW) / 2;
    return {
      x: startX + col * spacing + (Math.random() - 0.5) * 4,
      y: this.mapDef.startZoneY + row * spacing + Math.random() * 3,
    };
  }

  startCountdown() {
    this.audio = new GameAudio();
    this.audio.init();
    if (this.mapDef.bgmTrack) void this.audio.loadBgm(this.mapDef.bgmTrack);
    this.phase = 'countdown';
    this.countdownValue = 3;
    this.countdownTimer = 0;
    this.callbacks.onPhaseChange?.('countdown');
  }

  startImmediately() {
    if (!this.audio) {
      this.audio = new GameAudio();
      this.audio.init();
    }
    if (this.mapDef.bgmTrack) void this.audio.loadBgm(this.mapDef.bgmTrack);
    this.phase = 'running';
    this.isRunning = true;
    this.launchMarbles();
    this.callbacks.onPhaseChange?.('running');
  }

  private launchMarbles() {
    const shuffled = this.config.mode === 'team' ? shuffle(this.marbles) : this.marbles;
    shuffled.forEach((m, i) => {
      const vx = (Math.random() - 0.5) * 1.15;
      const vy = 0.85 + Math.random() * 0.75 + i * 0.006;
      Matter.Body.setVelocity(m.body, { x: vx, y: vy });
    });
  }

  private readonly loop = (ts: number) => {
    if (this.lastTime === 0) this.lastTime = ts;
    const raw = Math.min(ts - this.lastTime, 100);
    this.lastTime = ts;

    if (this.phase === 'countdown') {
      this.countdownTimer += raw;
      if (this.countdownTimer >= 1000) {
        this.countdownValue--;
        this.countdownTimer -= 1000;
        this.audio?.playCountdown(this.countdownValue);
        this.callbacks.onCountdown?.(this.countdownValue);
        if (this.countdownValue <= 0) {
          this.phase = 'running';
          this.isRunning = true;
          this.launchMarbles();
          this.callbacks.onPhaseChange?.('running');
        }
      }
    }

    if (this.isRunning) {
      this.accumulator += raw;
      while (this.accumulator >= FIXED_DELTA) {
        const prevTension = this.tensionScale;
        this.updateTensionScale();
        if (Math.abs(this.tensionScale - prevTension) > 0.05) {
          this.audio?.setBgmVolume(this.tensionScale);
        }
        const scaledDelta = FIXED_DELTA * this.tensionScale;
        this.updateMovingCircles();
        const subDelta = scaledDelta / 8;
        for (let step = 0; step < 8; step++) {
          const prevPos = this.marbles.map((m) => ({ x: m.body.position.x, y: m.body.position.y }));
          this.updateRotors(subDelta);
          Matter.Engine.update(this.engine, subDelta);
          for (let i = 0; i < this.marbles.length; i++) {
            const m = this.marbles[i];
            if (!m.finished) this.moveMarble(m, { ...m.body.position }, prevPos[i]);
          }
          this.resolveMarbleVsCircles();
          this.limitMarbleSpeed();
        }
        this.gameTime += FIXED_DELTA;
        this.accumulator -= FIXED_DELTA;
        this.checkGoals();
        this.updateRollingAudio();
        this.antiStuck();
        this.applySideLaneControl();
        this.applyBoostZones();
        this.applyItems();
        this.applyPoppers();
        this.applyBubbles();
        this.applyFinalApproachSpacing();
        this.applyFinishDrama();
        this.keepMarblesInBounds();
        this.limitMarbleSpeed();
        this.updateMarbleSpin();
        this.forceFinishTimedOut();
      }
    }

    this.updateCamera(raw);
    this.drawFrame(ts);
    this.rafId = requestAnimationFrame(this.loop);
  };

  private updateRotors(delta: number) {
    const rotors = this.mapDef.rotors ?? [];
    this.rotorBodies.forEach((body, i) => {
      const av = rotors[i]?.angularVelocity ?? 0;
      Matter.Body.setAngle(body, body.angle + av * (delta / 1000));
      Matter.Body.setAngularVelocity(body, av);
    });
  }

  private checkGoals() {
    const goalY = this.mapDef.goalY;
    for (const m of this.marbles) {
      if (!m.finished && m.body.position.y >= goalY) {
        m.finished = true;
        m.rank = ++this.rankCounter;
        if (m.rank === 1) this.firstGoalTime = this.gameTime;
        m.finishTime = this.gameTime;
        m.pulseT = this.gameTime;
        this.finishedMarbles.push(m);
        Matter.Composite.remove(this.engine.world, m.body);
        this.audio?.playGoal(m.rank);
        this.callbacks.onGoal?.(m.rank, m.student.name, m.color);
        this.maybeEndGame();
      }
    }
  }

  private antiStuck() {
    for (const m of this.marbles) {
      if (m.finished) continue;
      const dx = m.body.position.x - m.lastPos.x;
      const dy = m.body.position.y - m.lastPos.y;
      if (dx * dx + dy * dy < STUCK_THRESHOLD) {
        m.stuckTimer += FIXED_DELTA;
        if (m.stuckTimer > STUCK_DELAY) {
          const laneBias = m.body.position.x < this.mapDef.worldWidth / 2 ? 1 : -1;
          const randomX = (Math.random() - 0.5) * 0.0014;
          Matter.Body.applyForce(m.body, m.body.position, {
            x: laneBias * 0.0016 + randomX,
            y: 0.009,
          });
          Matter.Body.setVelocity(m.body, {
            x: m.body.velocity.x * 0.55 + laneBias * (0.65 + Math.random() * 0.35),
            y: Math.max(m.body.velocity.y, 3.5),
          });
          this.moveMarble(m, { x: m.body.position.x + laneBias * 6, y: m.body.position.y + 14 });
          this.clampMarbleToWorld(m);
          m.stuckTimer = 0;
        }
      } else {
        m.stuckTimer = 0;
      }

      if (m.body.position.y <= m.lastProgressY + 12) {
        m.progressTimer += FIXED_DELTA;
        const distToGoal = this.mapDef.goalY - m.body.position.y;
        const nearRotor = this.isNearRotor(m);

        // Fast-escape for marbles stuck near the finish zone — velocity only (no translate in narrow funnel)
        if (distToGoal < 350 && m.progressTimer > 450) {
          const cx = this.mapDef.worldWidth / 2;
          // Clamp horizontal component so marble doesn't tunnel through funnel walls
          const toCenter = Math.max(-2.5, Math.min(2.5, (cx - m.body.position.x) * 0.08));
          Matter.Body.setVelocity(m.body, { x: toCenter, y: MAX_SPEED });
          m.progressTimer = 0;
          m.lastProgressY = m.body.position.y;
        } else if (m.progressTimer > 800) {
          if (nearRotor) {
            // Velocity-only escape — no translate to avoid wall tunneling
            Matter.Body.setVelocity(m.body, { x: 0, y: MAX_SPEED });
          } else {
            const centerPull = (this.mapDef.worldWidth / 2 - m.body.position.x) * 0.000009;
            Matter.Body.applyForce(m.body, m.body.position, { x: centerPull, y: 0.01 });
            Matter.Body.setVelocity(m.body, {
              x: m.body.velocity.x * 0.62 + centerPull * 520,
              y: Math.max(m.body.velocity.y, 3.8),
            });
            this.moveMarble(m, { x: m.body.position.x, y: m.body.position.y + 30 });
            this.clampMarbleToWorld(m);
          }
          m.progressTimer = 0;
          m.lastProgressY = m.body.position.y;
        }
      } else {
        m.progressTimer = 0;
        m.lastProgressY = m.body.position.y;
      }
      m.lastPos = { x: m.body.position.x, y: m.body.position.y };
    }
  }

  private maybeEndGame() {
    const { mode, pickCount } = this.config;
    const winnerTarget = this.getWinnerTargetRank();
    const rewardTarget = this.getRewardSlots().length;
    const need =
      mode === 'single' ? winnerTarget
        : mode === 'multi' ? pickCount
          : mode === 'team' ? this.marbles.length
            : mode === 'reward' ? rewardTarget
              : this.marbles.length;

    if (!this.resultReadyEmitted && this.rankCounter >= need) {
      this.resultReadyEmitted = true;
      this.callbacks.onResultReady?.(this.createResult());
    }

    if (this.rankCounter >= this.marbles.length) {
      this.isRunning = false;
      this.phase = 'finished';
      this.callbacks.onPhaseChange?.('finished');
      setTimeout(() => this.emitResult(), 800);
    }
  }

  private getWinnerTargetRank() {
    if (this.config.mode !== 'single') return 1;
    if (this.config.winnerRule === 'last') return this.marbles.length;
    if (this.config.winnerRule === 'custom') {
      return Math.max(1, Math.min(this.config.winnerRank ?? 1, this.marbles.length));
    }
    return 1;
  }

  private applyBoostZones() {
    const zones = this.mapDef.boostZones ?? [];
    if (zones.length === 0) return;

    for (const m of this.marbles) {
      if (m.finished) continue;
      for (const z of zones) {
        const inside =
          m.body.position.x >= z.x - z.width / 2 &&
          m.body.position.x <= z.x + z.width / 2 &&
          m.body.position.y >= z.y - z.height / 2 &&
          m.body.position.y <= z.y + z.height / 2;
        if (!inside) continue;
        Matter.Body.applyForce(m.body, m.body.position, {
          x: z.forceX ?? 0,
          y: z.forceY ?? 0.006,
        });
      }
    }
  }

  private applySideLaneControl() {
    // Disabled side lane control as the map's sideGuards correctly handle bouncing
  }

  /** Get the animated position of an item (orbiting around its base) */
  private getItemPosition(item: { x: number; y: number; id: string }, t: number): { x: number; y: number } {
    const seed = item.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const orbitRadius = 48;
    const speed = 0.0014 + (seed % 5) * 0.00022;
    const phase = (seed % 100) * 0.0628;
    return {
      x: item.x + Math.sin(t * speed + phase) * orbitRadius,
      y: item.y + Math.cos(t * speed * 1.3 + phase * 0.7) * (orbitRadius * 0.55),
    };
  }

  private applyItems() {
    if (!this.config.itemsEnabled) return;
    const items = this.mapDef.itemZones ?? [];
    if (items.length === 0) return;

    for (const m of this.marbles) {
      if (m.finished) continue;
      for (const item of items) {
        if (this.consumedItems.has(item.id)) continue;
        if (m.usedItems.has(item.id)) continue;
        const pos = this.getItemPosition(item, this.gameTime);
        const dx = m.body.position.x - pos.x;
        const dy = m.body.position.y - pos.y;
        if (dx * dx + dy * dy > item.radius * 1.25 * item.radius * 1.25) continue;

        m.usedItems.add(item.id);
        this.consumedItems.add(item.id);
        m.pulseT = this.gameTime;
        this.addEffectToast(m, this.itemGlyph(item.kind), this.itemColor(item.kind));

        if (item.kind === 'boost') {
          Matter.Body.setVelocity(m.body, {
            x: m.body.velocity.x * 0.8,
            y: Math.max(m.body.velocity.y + 2.1, 3.8),
          });
          Matter.Body.applyForce(m.body, m.body.position, { x: 0, y: 0.011 });
        } else if (item.kind === 'slow') {
          if (m.shielded) {
            m.shielded = false;
          } else {
            Matter.Body.setVelocity(m.body, {
              x: m.body.velocity.x * 0.35,
              y: Math.max(1.2, m.body.velocity.y * 0.28),
            });
            Matter.Body.applyForce(m.body, m.body.position, { x: 0, y: -0.018 });
          }
        } else if (item.kind === 'chaos') {
          if (m.shielded) {
            m.shielded = false;
          } else {
            const direction = m.body.position.x < this.mapDef.worldWidth / 2 ? 1 : -1;
            Matter.Body.setVelocity(m.body, {
              x: direction * (2.4 + Math.random() * 1.4),
              y: Math.max(m.body.velocity.y, 3),
            });
          }
        } else if (item.kind === 'shield') {
          m.shielded = true;
        } else if (item.kind === 'swap') {
          // Teleport to just ahead of the current leader
          const leader = [...this.marbles]
            .filter((m2) => !m2.finished && m2 !== m)
            .sort((a, b) => b.body.position.y - a.body.position.y)[0];
          if (leader) {
            this.moveMarble(m, {
              x: leader.body.position.x + (Math.random() - 0.5) * 20,
              y: leader.body.position.y - 25,
            });
            Matter.Body.setVelocity(m.body, { ...leader.body.velocity });
          } else {
            Matter.Body.applyForce(m.body, m.body.position, { x: 0, y: 0.014 });
          }
        } else if (item.kind === 'teleport') {
          const jumpY = 350 + Math.random() * 200;
          this.moveMarble(m, { x: m.body.position.x, y: Math.min(this.mapDef.goalY - 40, m.body.position.y + jumpY) });
          Matter.Body.setVelocity(m.body, {
            x: m.body.velocity.x * 0.5,
            y: Math.max(m.body.velocity.y, 3.5),
          });
        } else if (item.kind === 'vortex') {
          const centerPull = (this.mapDef.worldWidth / 2 - m.body.position.x) * 0.018;
          Matter.Body.setVelocity(m.body, {
            x: m.body.velocity.x * 0.25 + centerPull,
            y: Math.max(m.body.velocity.y + 1.8, 3.2),
          });
          for (const other of this.marbles) {
            if (other.finished || other === m) continue;
            const deltaY = Math.abs(other.body.position.y - m.body.position.y);
            if (deltaY > 260) continue;
            Matter.Body.setVelocity(other.body, {
              x: other.body.velocity.x * 0.72,
              y: other.body.velocity.y * 0.82,
            });
          }
        } else if (item.kind === 'anchor') {
          if (m.shielded) {
            m.shielded = false;
          } else {
            Matter.Body.setVelocity(m.body, {
              x: m.body.velocity.x * 0.18,
              y: Math.max(0.7, m.body.velocity.y * 0.18),
            });
            Matter.Body.applyForce(m.body, m.body.position, { x: 0, y: -0.025 });
          }
        }
      }
    }
  }

  private updateMovingCircles() {
    const t = this.gameTime;
    for (const peg of this.pegBodies) {
      const ampX = peg.def.radius >= 12 ? 1.4 : 2.2;
      const ampY = peg.def.radius >= 12 ? 1.0 : 1.6;
      Matter.Body.setPosition(peg.body, {
        x: peg.baseX + Math.sin(t * 0.0012 + peg.seed) * ampX,
        y: peg.baseY + Math.cos(t * 0.001 + peg.seed * 0.7) * ampY,
      });
    }

    for (const state of this.bubbleStates.values()) {
      if (state.body === null) continue;
      Matter.Body.setPosition(state.body, {
        x: state.baseX + Math.sin(t * 0.001 + state.seed) * 3.2,
        y: state.baseY + Math.cos(t * 0.00125 + state.seed * 0.6) * 2.4,
      });
    }
  }

  private applyPoppers() {
    const poppers = this.mapDef.poppers ?? [];
    if (poppers.length === 0) return;

    for (const m of this.marbles) {
      if (m.finished) continue;
      for (const popper of poppers) {
        if (this.usedPopperIds.has(popper.id)) continue;
        const dx = m.body.position.x - popper.x;
        const dy = m.body.position.y - popper.y;
        if (dx * dx + dy * dy > popper.radius * 1.25 * popper.radius * 1.25) continue;
        this.usedPopperIds.add(popper.id);
        const len = Math.max(1, Math.hypot(dx, dy));
        Matter.Body.setVelocity(m.body, {
          x: m.body.velocity.x * 0.65 + (dx / len) * popper.force * 1.95,
          y: Math.max(3.8, m.body.velocity.y + (dy / len) * popper.force * 0.9),
        });
        Matter.Body.applyForce(m.body, m.body.position, {
          x: (dx / len) * 0.035,
          y: 0.018,
        });
        this.audio?.playPopper();
        this.addEffectToast(m, 'POP!', '#FDBA74');
        m.pulseT = this.gameTime;
      }
    }
  }

  private applyBubbles() {
    if (this.bubbleStates.size === 0) return;
    const marbleR = this.marbleRadius(this.config.students.length);

    for (const state of this.bubbleStates.values()) {
      if (state.body === null) continue;
      const { x, y } = state.body.position;
      // Slightly expanded trigger for tunneling resistance without over-launching.
      const triggerDist = (marbleR + state.def.radius) * 1.35;

      for (const m of this.marbles) {
        if (m.finished) continue;
        const dx = m.body.position.x - x;
        const dy = m.body.position.y - y;
        if (dx * dx + dy * dy > triggerDist * triggerDist) continue;

        // Pop the bubble
        Matter.Composite.remove(this.engine.world, state.body);
        state.burstX = x;
        state.burstY = y;
        state.burstTime = this.gameTime;
        state.body = null;

        // Bubble is a one-shot upward bounce pad. Keep it dramatic but not map-breaking.
        const len = Math.max(1, Math.hypot(dx, dy));
        Matter.Body.setVelocity(m.body, {
          x: m.body.velocity.x * 0.42 + (dx / len) * 3.2,
          y: Math.min(m.body.velocity.y, -7.4),
        });
        Matter.Body.applyForce(m.body, m.body.position, {
          x: (dx / len) * 0.055,
          y: -0.08,
        });
        m.bubbleBounceTime = this.gameTime;
        this.audio?.playBubble();
        this.addEffectToast(m, 'BUBBLE!', '#C4B5FD');
        m.pulseT = this.gameTime;
        break;
      }
    }
  }

  private updateRollingAudio() {
    if (!this.audio) return;
    let totalSpeed = 0;
    let count = 0;
    for (const m of this.marbles) {
      if (m.finished) continue;
      totalSpeed += Math.hypot(m.body.velocity.x, m.body.velocity.y);
      count++;
    }
    const avgSpeed = count > 0 ? totalSpeed / count : 0;
    this.audio.setRollingIntensity(avgSpeed / MAX_SPEED);
  }

  private updateMarbleSpin() {
    const radius = this.marbleRadius(this.config.students.length);
    for (const m of this.marbles) {
      if (m.finished) continue;
      const speed = Math.hypot(m.body.velocity.x, m.body.velocity.y);
      const direction = m.body.velocity.x >= 0 ? 1 : -1;
      m.spin += direction * (speed / Math.max(radius, 1)) * 0.8;
    }
  }

  private updateTensionScale() {
    if (this.rankCounter > 0) {
      if (this.firstGoalTime >= 0) {
        const elapsed = this.gameTime - this.firstGoalTime;
        if (elapsed < FIRST_GOAL_DRAMA_MS) {
          const t = elapsed / FIRST_GOAL_DRAMA_MS;
          this.tensionScale = 0.06 + t * 0.94;
          return;
        }
      }
      this.tensionScale = 1.0;
      return;
    }
    const TENSION_DIST = 400;
    let leadY = -Infinity;
    for (const m of this.marbles) {
      if (!m.finished && m.body.position.y > leadY) leadY = m.body.position.y;
    }
    if (leadY === -Infinity) { this.tensionScale = 1.0; return; }
    const dist = this.mapDef.goalY - leadY;
    if (dist > TENSION_DIST || dist < 0) {
      this.tensionScale = 1.0;
    } else {
      // Ease from 1.0 at 400px away → 0.12 at the line
      this.tensionScale = Math.max(0.12, dist / TENSION_DIST);
    }
  }

  private applyFinishDrama() {
    for (const m of this.marbles) {
      if (m.finished) continue;
      const dist = this.mapDef.goalY - m.body.position.y;
      if (dist > 720 || dist < 0) continue;

      const pressure = 1 - dist / 720;
      // Progressively tighter speed caps: prevents a pack of marbles arriving at
      // the funnel entrance all at once and overwhelming the constraint solver.
      const maxY = dist < 180 ? 1.6 : dist < 320 ? 2.2 : 4.2;
      Matter.Body.setVelocity(m.body, {
        x: m.body.velocity.x * (0.99 - pressure * 0.012),
        y: Math.min(m.body.velocity.y * (0.986 - pressure * 0.016), maxY),
      });
      Matter.Body.applyForce(m.body, m.body.position, {
        x: (this.mapDef.worldWidth / 2 - m.body.position.x) * 0.0000035,
        y: 0.0018 + pressure * 0.001,
      });
    }
  }

  /** Push marbles apart when crowded in the final approach zone to prevent pile-up
   *  from overwhelming the constraint solver and forcing marbles through funnel walls. */
  private applyFinalApproachSpacing() {
    const approachY = this.mapDef.goalY - 640;
    const marbleR = this.marbleRadius(this.config.students.length);
    const minSep = marbleR * 2.5;
    const active: MarbleData[] = [];
    for (const m of this.marbles) {
      if (!m.finished && m.body.position.y > approachY) active.push(m);
    }
    if (active.length < 2) return;
    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i];
        const b = active[j];
        const dx = b.body.position.x - a.body.position.x;
        const dy = b.body.position.y - a.body.position.y;
        const dist = Math.hypot(dx, dy);
        if (dist > minSep || dist < 0.001) continue;
        // Stronger repulsion to prevent constraint-solver-driven tunneling
        const overlap = (minSep - dist) / minSep;
        const strength = overlap * 0.0025;
        const nx = dx / dist;
        Matter.Body.applyForce(a.body, a.body.position, { x: -nx * strength, y: 0 });
        Matter.Body.applyForce(b.body, b.body.position, { x: nx * strength, y: 0 });
      }
    }
  }

  private addEffectToast(m: MarbleData, text: string, color: string) {
    this.effectToasts.push({
      text: `${m.student.name} ${text}`,
      x: m.body.position.x,
      y: m.body.position.y,
      color,
      start: this.gameTime,
    });
    if (this.effectToasts.length > 18) this.effectToasts.shift();
  }

  private keepMarblesInBounds() {
    const radius = this.marbleRadius(this.config.students.length);
    const minX = radius;
    const maxX = this.mapDef.worldWidth - radius;
    for (const m of this.marbles) {
      if (m.finished) continue;
      const px = m.body.position.x;
      const vx = m.body.velocity.x;

      if (px < minX) {
        // Outside left boundary — clamp and force rightward velocity
        this.moveMarble(m, { x: minX, y: m.body.position.y });
        Matter.Body.setVelocity(m.body, {
          x: Math.max(Math.abs(vx) * 0.5, 0.5),
          y: m.body.velocity.y,
        });
      } else if (px > maxX) {
        // Outside right boundary — clamp and force leftward velocity
        this.moveMarble(m, { x: maxX, y: m.body.position.y });
        Matter.Body.setVelocity(m.body, {
          x: -Math.max(Math.abs(vx) * 0.5, 0.5),
          y: m.body.velocity.y,
        });
      } else if (px < minX + radius && vx < -1) {
        // Near left wall moving outward — cancel leftward velocity to stop tunneling
        Matter.Body.setVelocity(m.body, { x: 0, y: m.body.velocity.y });
      } else if (px > maxX - radius && vx > 1) {
        // Near right wall moving outward — cancel rightward velocity to stop tunneling
        Matter.Body.setVelocity(m.body, { x: 0, y: m.body.velocity.y });
      }
    }
  }

  private isNearRotor(m: (typeof this.marbles)[0]): boolean {
    const rotors = this.mapDef.rotors ?? [];
    return this.rotorBodies.some((rb, i) => {
      const halfW = (rotors[i]?.width ?? 128) / 2;
      const dx = m.body.position.x - rb.position.x;
      const dy = m.body.position.y - rb.position.y;
      return Math.hypot(dx, dy) < halfW + 20;
    });
  }

  private moveMarble(m: MarbleData, target: { x: number; y: number }, from = { ...m.body.position }) {
    const result = moveInsideWalls(from, target, m.body.velocity,
      this.marbleRadius(this.config.students.length), this.containmentWalls);
    Matter.Body.setPosition(m.body, result.position);
    Matter.Body.setVelocity(m.body, result.velocity);
  }

  /** Per-sub-step exact push-out for marbles overlapping pegs (circle vs circle).
   *  After each push, verifies the new position is not inside a wall —
   *  if it would cause a wall violation the push is skipped entirely. */
  private resolveMarbleVsCircles() {
    const marbleR = this.marbleRadius(this.config.students.length);
    for (const m of this.marbles) {
      if (m.finished) continue;
      for (const peg of this.pegBodies) {
        const dx = m.body.position.x - peg.body.position.x;
        const dy = m.body.position.y - peg.body.position.y;
        const distSq = dx * dx + dy * dy;
        const minDist = marbleR + peg.def.radius;
        if (distSq >= minDist * minDist) continue;

        const dist = Math.sqrt(distSq) || 0.01;
        const nx = dx / dist;
        const ny = dy / dist;
        const pen = minDist - dist + 0.5;

        // Pre-check: would this push land inside a wall? If so, skip — let the
        // sub-step wall-crossing check handle it rather than punching through.
        const candidateX = m.body.position.x + nx * pen;
        const candidateY = m.body.position.y + ny * pen;
        if (
          this.wallBodyList.length > 0 &&
          Matter.Query.point(this.wallBodyList, { x: candidateX, y: candidateY }).length > 0
        ) continue;

        this.moveMarble(m, { x: candidateX, y: candidateY });
        // Cancel remaining inward velocity component
        const vn = m.body.velocity.x * nx + m.body.velocity.y * ny;
        if (vn < 0) {
          Matter.Body.setVelocity(m.body, {
            x: m.body.velocity.x - vn * nx,
            y: m.body.velocity.y - vn * ny,
          });
        }
        this.clampMarbleToWorld(m);
      }
    }
  }

  private clampMarbleToWorld(m: (typeof this.marbles)[0]) {
    const radius = this.marbleRadius(this.config.students.length);
    const minX = radius;
    const maxX = this.mapDef.worldWidth - radius;
    const maxY = this.mapDef.worldHeight - radius;
    const x = Math.min(maxX, Math.max(minX, m.body.position.x));
    const y = Math.min(maxY, m.body.position.y);
    if (x !== m.body.position.x || y !== m.body.position.y) {
      this.moveMarble(m, { x, y });
    }
  }

  private limitMarbleSpeed() {
    for (const m of this.marbles) {
      if (m.finished) continue;
      // Allow strong upward velocity for 600ms after a bubble bounce
      if (this.gameTime - m.bubbleBounceTime < 600 && m.body.velocity.y < -2.5) continue;
      const distToGoal = this.mapDef.goalY - m.body.position.y;
      const speed = Math.hypot(m.body.velocity.x, m.body.velocity.y);
      const cap = distToGoal < 760 ? 4.4 : MAX_SPEED;
      if (speed > cap) {
        const scale = cap / speed;
        Matter.Body.setVelocity(m.body, {
          x: m.body.velocity.x * scale,
          y: m.body.velocity.y * scale,
        });
      }
      // Hard cap on horizontal speed in the funnel zone to prevent wall tunneling
      // when marbles pile up and the constraint solver pushes them sideways
      if (distToGoal < 480) {
        const maxVx = distToGoal < 200 ? 1.8 : 2.8;
        const vx = m.body.velocity.x;
        if (Math.abs(vx) > maxVx) {
          Matter.Body.setVelocity(m.body, {
            x: Math.sign(vx) * maxVx,
            y: m.body.velocity.y,
          });
        }
      }
    }
  }

  private forceFinishTimedOut() {
    if (this.phase !== 'running') return;
    if (this.gameTime < FORCE_FINISH_MS) return;
    const remaining = this.marbles
      .filter((m) => !m.finished)
      .sort((a, b) => b.body.position.y - a.body.position.y);
    for (const m of remaining) {
      m.finished = true;
      m.rank = ++this.rankCounter;
      m.finishTime = this.gameTime;
      this.finishedMarbles.push(m);
      Matter.Composite.remove(this.engine.world, m.body);
      this.callbacks.onGoal?.(m.rank, m.student.name, m.color);
    }
    this.maybeEndGame();
  }

  private emitResult() {
    this.callbacks.onFinish?.(this.createResult());
  }

  private createResult(): GameResult {
    const rewardSlots = this.config.mode === 'reward' ? this.getRewardSlots() : [];
    const rankings: MarbleResult[] = this.finishedMarbles.map((m) => ({
      rank: m.rank,
      studentId: m.student.id,
      name: m.student.name,
      color: m.color,
      reward: rewardSlots[m.rank - 1],
    }));
    // Append unfinished
    let extra = this.rankCounter;
    for (const m of this.marbles) {
      if (!m.finished) {
        rankings.push({
          rank: ++extra,
          studentId: m.student.id,
          name: m.student.name,
          color: m.color,
          reward: rewardSlots[extra - 1],
        });
      }
    }

    const result: GameResult = {
      mode: this.config.mode,
      config: this.config,
      rankings,
      timestamp: Date.now(),
    };

    if (this.config.mode === 'team') {
      const n = this.config.teamCount || 2;
      const teams = Array.from({ length: n }, (_, i) => ({ teamName: `${i + 1}팀`, members: [] as string[] }));
      rankings.forEach((r, i) => teams[i % n].members.push(r.name));
      result.teams = teams;
    }

    return result;
  }

  private getRewardSlots(): string[] {
    return (this.config.rewards ?? []).flatMap((reward) => {
      const name = reward.name.trim();
      const count = Math.max(0, Math.floor(reward.count || 0));
      return name ? Array.from({ length: count }, () => name) : [];
    });
  }

  // ─── Rendering ────────────────────────────────────────────────────
  private drawFrame(ts: number) {
    const ctx = this.ctx;
    const rect = this.canvas.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;
    if (W === 0 || H === 0) return;

    ctx.clearRect(0, 0, W, H);
    this.drawBackground(ctx, W, H, ts);

    ctx.save();
    const effectiveScale = this.getEffectiveScale();
    ctx.translate(this.offsetX, -this.cameraY * effectiveScale);
    ctx.scale(effectiveScale, effectiveScale);

    this.drawMap(ctx, ts);
    this.drawMarbles(ctx, ts);
    this.drawEffectToasts(ctx);

    ctx.restore();
    this.drawMinimap(ctx, W, H);

    // countdown overlay
    if (this.phase === 'countdown' && this.countdownValue > 0) {
      this.drawCountdown(ctx, W, H);
    }

    // idle prompt
    if (this.phase === 'idle') {
      this.drawStartPrompt(ctx, W, H);
    }
  }

  private drawBackground(ctx: CanvasRenderingContext2D, W: number, H: number, ts: number) {
    const style = this.mapDef.bgStyle ?? 'space';

    // Each style: base gradient + optional animated accent
    type Stop = [number, string];
    const configs: Record<string, { stops: Stop[]; accent?: string; grid?: boolean; stars?: boolean }> = {
      arcade: {
        stops: [[0, '#0d0221'], [0.5, '#180838'], [1, '#0a041f']],
        accent: 'rgba(94,234,212,0.045)',
        grid: true,
      },
      desert: {
        stops: [[0, '#1a0a00'], [0.45, '#3d1800'], [1, '#0e0800']],
        accent: 'rgba(251,146,60,0.06)',
      },
      dungeon: {
        stops: [[0, '#020b06'], [0.5, '#041a0c'], [1, '#010a05']],
        accent: 'rgba(52,211,153,0.035)',
        grid: true,
      },
      pastel: {
        stops: [[0, '#1a0a2e'], [0.5, '#2d0a3e'], [1, '#0e0518']],
        accent: 'rgba(251,113,133,0.06)',
      },
      frost: {
        stops: [[0, '#020b18'], [0.5, '#041830'], [1, '#02080f']],
        accent: 'rgba(147,197,253,0.055)',
        stars: true,
      },
      space: {
        stops: [[0, '#080c18'], [1, '#0d1a2e']],
        stars: true,
      },
    };

    const cfg = configs[style] ?? configs.space;
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    for (const [pos, color] of cfg.stops) bg.addColorStop(pos, color);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Subtle grid lines
    if (cfg.grid) {
      const gridSize = 48;
      const phase = (ts * 0.012) % gridSize;
      ctx.save();
      ctx.strokeStyle = cfg.accent ?? 'rgba(255,255,255,0.03)';
      ctx.lineWidth = 0.5;
      for (let x = phase; x < W; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = phase; y < H; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
      ctx.restore();
    }

    // Animated accent pulse (radial from center)
    if (cfg.accent) {
      const pulse = Math.sin(ts * 0.0008) * 0.5 + 0.5;
      const rad = ctx.createRadialGradient(W / 2, H * 0.35, 0, W / 2, H * 0.35, W * 0.75);
      rad.addColorStop(0, cfg.accent.replace(/[\d.]+\)$/, `${0.08 + pulse * 0.06})`));
      rad.addColorStop(1, 'transparent');
      ctx.fillStyle = rad;
      ctx.fillRect(0, 0, W, H);
    }

    // Stars for frost/space
    if (cfg.stars) {
      ctx.save();
      // Use a seeded sequence so stars don't flicker every frame
      const starCount = 55;
      for (let i = 0; i < starCount; i++) {
        const sx = ((i * 137.508 + 43) % W + W) % W;
        const sy = ((i * 97.31 + 17) % H + H) % H;
        const twinkle = Math.sin(ts * 0.001 + i * 1.7) * 0.4 + 0.6;
        const r = 0.6 + (i % 3) * 0.4;
        ctx.globalAlpha = twinkle * 0.55;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  private drawMap(ctx: CanvasRenderingContext2D, ts: number) {
    const { walls, goalY } = this.mapDef;

    // Goal line
    const pulse = Math.sin(ts * 0.003) * 0.4 + 0.6;
    ctx.save();
    ctx.shadowColor = `rgba(251, 113, 133, ${pulse})`;
    ctx.shadowBlur = 18;
    ctx.strokeStyle = `rgba(251, 113, 133, ${0.75 + pulse * 0.25})`;
    ctx.lineWidth = 3;
    ctx.setLineDash([16, 10]);
    ctx.beginPath();
    ctx.moveTo(20, goalY);
    ctx.lineTo(this.mapDef.worldWidth - 20, goalY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = 'bold 13px Pretendard, sans-serif';
    ctx.fillStyle = 'rgba(251,113,133,0.9)';
    ctx.fillText('FINISH', 28, goalY - 7);
    ctx.restore();

    // Map walls
    for (const w of walls) {
      ctx.save();
      ctx.translate(w.x, w.y);
      if (w.angle) ctx.rotate(w.angle);
      
      // Draw as a solid glowing neon line, so overlapping rectangles merge seamlessly
      ctx.shadowColor = '#5EEAD4';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#5EEAD4'; // Solid bright cyan
      ctx.fillRect(-w.width / 2, -w.height / 2, w.width, w.height);
      
      ctx.restore();
    }

    // Pegs — the physics bodies also drift slightly, so visuals use body positions.
    for (const peg of this.pegBodies) {
      const p = peg.def;
      const { x, y } = peg.body.position;
      ctx.save();
      ctx.shadowColor = '#5EEAD4';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(x, y, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = '#1a3a50';
      ctx.fill();
      ctx.strokeStyle = '#5EEAD4';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }

    const rotors = this.mapDef.rotors ?? [];
    for (let i = 0; i < this.rotorBodies.length; i++) {
      const body = this.rotorBodies[i];
      const rotor = rotors[i];
      ctx.save();
      ctx.translate(body.position.x, body.position.y);
      ctx.rotate(body.angle);
      ctx.shadowColor = '#FBBF24';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#7C3AED';
      ctx.strokeStyle = '#FBBF24';
      ctx.lineWidth = 2;
      const w = rotor?.width ?? 96;
      const h = rotor?.height ?? 10;
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.strokeRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    }

    if (this.config.itemsEnabled) {
      this.drawItems(ctx, ts);
    }
    this.drawPoppers(ctx, ts);
    this.drawBubbles(ctx, ts);
    
    // Boundary wall lines (Removed completely to allow zigzag walls to act as true borders)
  }

  private drawItems(ctx: CanvasRenderingContext2D, ts: number) {
    const items = this.mapDef.itemZones ?? [];

    for (const item of items) {
      const consumed = this.consumedItems.has(item.id);
      const activeAlpha = consumed ? 0.08 : 1;
      const pulse = Math.sin(ts * 0.007 + item.y * 0.01) * 0.26 + 0.82;
      const colorMap: Record<string, string> = {
        boost: this.itemColor('boost'), slow: this.itemColor('slow'), chaos: this.itemColor('chaos'),
        swap: this.itemColor('swap'), shield: this.itemColor('shield'), teleport: this.itemColor('teleport'),
        vortex: this.itemColor('vortex'), anchor: this.itemColor('anchor'),
      };
      const color = colorMap[item.kind] ?? '#FBBF24';
      const glyph = this.itemGlyph(item.kind);

      // Use animated position
      const pos = this.getItemPosition(item, this.gameTime);

      const dr = item.radius * 1.25;
      ctx.save();
      ctx.globalAlpha = activeAlpha;
      ctx.translate(pos.x, pos.y);

      // Glowing trail ring
      const trailAlpha = 0.22 + Math.sin(ts * 0.005) * 0.14;
      ctx.beginPath();
      ctx.arc(0, 0, dr * 1.75, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = activeAlpha * trailAlpha;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.globalAlpha = activeAlpha;

      // Main circle
      ctx.shadowColor = color;
      ctx.shadowBlur = 26 * pulse;
      ctx.beginPath();
      ctx.arc(0, 0, dr * pulse, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(8,12,18,0.85)';
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = color;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Emoji icon
      ctx.font = `${Math.round(dr * 0.9)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(glyph, 0, 1);
      ctx.restore();
    }
  }

  private itemGlyph(kind: string) {
    return ({
      boost: '⚡',
      slow: '🧊',
      chaos: '🌀',
      swap: '🔀',
      shield: '🛡️',
      teleport: '🌠',
      vortex: '◎',
      anchor: '⬇',
    } as Record<string, string>)[kind] ?? '?';
  }

  private itemColor(kind: string) {
    return ({
      boost: '#34D399',
      slow: '#FB7185',
      chaos: '#FBBF24',
      swap: '#818CF8',
      shield: '#60A5FA',
      teleport: '#F472B6',
      vortex: '#22D3EE',
      anchor: '#F97316',
    } as Record<string, string>)[kind] ?? '#FBBF24';
  }

  private drawEffectToasts(ctx: CanvasRenderingContext2D) {
    const now = this.gameTime;
    this.effectToasts = this.effectToasts.filter((toast) => now - toast.start < 1300);
    for (const toast of this.effectToasts) {
      const t = (now - toast.start) / 1300;
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1 - t);
      ctx.translate(toast.x, toast.y - 34 - t * 42);
      ctx.font = '900 16px Pretendard, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 5;
      ctx.strokeStyle = 'rgba(2,6,23,0.88)';
      ctx.strokeText(toast.text, 0, 0);
      ctx.fillStyle = toast.color;
      ctx.fillText(toast.text, 0, 0);
      ctx.restore();
    }
  }

  private drawPoppers(ctx: CanvasRenderingContext2D, ts: number) {
    const poppers = this.mapDef.poppers ?? [];
    if (poppers.length === 0) return;

    for (const popper of poppers) {
      const isUsed = this.usedPopperIds.has(popper.id);
      const dr = popper.radius * 1.25;
      const pulse = Math.sin(ts * 0.008 + popper.y * 0.02) * 0.18 + 0.9;
      ctx.save();
      ctx.globalAlpha = isUsed ? 0.18 : 0.9;
      ctx.translate(popper.x, popper.y);
      ctx.shadowColor = '#F97316';
      ctx.shadowBlur = isUsed ? 0 : 16;
      ctx.beginPath();
      ctx.arc(0, 0, dr * pulse, 0, Math.PI * 2);
      ctx.fillStyle = isUsed ? 'rgba(71,85,105,0.45)' : 'rgba(249,115,22,0.22)';
      ctx.fill();
      ctx.strokeStyle = isUsed ? 'rgba(148,163,184,0.4)' : '#FDBA74';
      ctx.lineWidth = 3;
      ctx.stroke();
      if (!isUsed) {
        ctx.fillStyle = '#FDBA74';
        ctx.font = `900 ${Math.max(12, dr * 0.82)}px Pretendard, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('!', 0, 1);
      }
      ctx.restore();
    }
  }

  private drawBubbles(ctx: CanvasRenderingContext2D, ts: number) {
    if (this.bubbleStates.size === 0) return;
    const BURST_DURATION = 600;

    for (const state of this.bubbleStates.values()) {
      if (state.body !== null) {
        // Active bubble — colorful translucent circle with pulse
        const { x, y } = state.body.position;
        const r = state.def.radius * 1.25;
        const pulse = Math.sin(ts * 0.007 + state.def.x * 0.05) * 0.15 + 0.85;
        ctx.save();
        ctx.shadowColor = '#A78BFA';
        ctx.shadowBlur = 18 * pulse;
        ctx.beginPath();
        ctx.arc(x, y, r * pulse, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.05, x, y, r * pulse);
        grad.addColorStop(0, 'rgba(196,181,253,0.55)');
        grad.addColorStop(0.6, 'rgba(139,92,246,0.35)');
        grad.addColorStop(1, 'rgba(109,40,217,0.1)');
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.strokeStyle = `rgba(196,181,253,${0.7 * pulse})`;
        ctx.lineWidth = 2;
        ctx.stroke();
        // Inner highlight
        ctx.beginPath();
        ctx.arc(x - r * 0.28, y - r * 0.32, r * 0.22, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.38)';
        ctx.fill();
        ctx.restore();
      } else if (state.burstTime >= 0) {
        // Burst animation — expanding ring that fades out
        const elapsed = this.gameTime - state.burstTime;
        if (elapsed > BURST_DURATION) continue;
        const t = elapsed / BURST_DURATION;
        const r = state.def.radius * (1 + t * 3.5);
        ctx.save();
        ctx.globalAlpha = (1 - t) * 0.85;
        ctx.beginPath();
        ctx.arc(state.burstX, state.burstY, r, 0, Math.PI * 2);
        ctx.strokeStyle = '#C4B5FD';
        ctx.lineWidth = 3 * (1 - t);
        ctx.stroke();
        // Second inner ring
        ctx.globalAlpha = (1 - t) * 0.5;
        ctx.beginPath();
        ctx.arc(state.burstX, state.burstY, r * 0.55, 0, Math.PI * 2);
        ctx.strokeStyle = '#EDE9FE';
        ctx.lineWidth = 2 * (1 - t);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  private drawMinimap(ctx: CanvasRenderingContext2D, _canvasW: number, canvasH: number) {
    const miniW = 84;
    const miniH = Math.min(canvasH - 34, 360);
    if (miniH < 140) return;

    const x = 14;
    const y = 14;
    const sx = miniW / this.mapDef.worldWidth;
    const sy = miniH / this.mapDef.worldHeight;
    const wallScale = Math.max(0.6, Math.min(sx, sy));

    ctx.save();
    ctx.globalAlpha = 0.94;
    ctx.fillStyle = 'rgba(3,7,18,0.78)';
    ctx.strokeStyle = 'rgba(34,211,238,0.45)';
    ctx.lineWidth = 1;
    this.roundRect(ctx, x, y, miniW, miniH, 8);
    ctx.fill();
    ctx.stroke();
    ctx.clip();

    ctx.translate(x, y);
    ctx.fillStyle = 'rgba(34,211,238,0.08)';
    ctx.fillRect(0, 0, miniW, miniH);

    ctx.save();
    ctx.scale(sx, sy);
    ctx.strokeStyle = 'rgba(125,211,252,0.46)';
    ctx.lineWidth = 1 / wallScale;
    for (const w of this.mapDef.walls) {
      ctx.save();
      ctx.translate(w.x, w.y);
      if (w.angle) ctx.rotate(w.angle);
      ctx.strokeRect(-w.width / 2, -w.height / 2, w.width, w.height);
      ctx.restore();
    }

    ctx.fillStyle = 'rgba(250,204,21,0.62)';
    for (const r of this.mapDef.rotors ?? []) {
      ctx.save();
      ctx.translate(r.x, r.y);
      ctx.rotate(r.angle ?? 0);
      ctx.fillRect(-r.width / 2, -r.height / 2, r.width, r.height);
      ctx.restore();
    }

    ctx.fillStyle = 'rgba(255,255,255,0.72)';
    const dotRadius = Math.max(10, this.marbleRadius(this.config.students.length));
    for (const m of this.marbles) {
      if (m.finished) continue;
      ctx.beginPath();
      ctx.arc(m.body.position.x, m.body.position.y, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = 'rgba(251,113,133,0.9)';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(0, this.mapDef.goalY);
    ctx.lineTo(this.mapDef.worldWidth, this.mapDef.goalY);
    ctx.stroke();
    ctx.restore();

    const effectiveScale = this.getEffectiveScale();
    const viewH = canvasH / Math.max(effectiveScale, 0.001);
    const viewY = this.cameraY * sy;
    const viewBoxH = Math.min(miniH, viewH * sy);
    ctx.strokeStyle = 'rgba(255,255,255,0.92)';
    ctx.lineWidth = 2;
    ctx.strokeRect(2, viewY + 2, miniW - 4, Math.max(12, viewBoxH - 4));

    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    ctx.font = '700 10px Pretendard, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('MAP', miniW / 2, 14);
    ctx.restore();
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private updateCamera(deltaMs: number) {
    const rect = this.canvas.getBoundingClientRect();
    const visibleH = rect.height / Math.max(this.getEffectiveScale(), 0.001);
    if (this.hoverCameraY !== null) {
      this.targetCameraZoom = 1;
      this.cameraZoom += (this.targetCameraZoom - this.cameraZoom) * Math.min(1, deltaMs / 260);
      this.updateDrawOffset(rect.width);
      const maxY = Math.max(0, this.mapDef.worldHeight - visibleH);
      this.cameraY += (Math.min(maxY, Math.max(0, this.hoverCameraY)) - this.cameraY) * Math.min(1, deltaMs / 120);
      return;
    }
    const active = this.marbles.filter((m) => !m.finished);
    let targetY = 0;
    if (this.phase === 'finished') {
      const focus = this.finishedMarbles[0];
      const focusY = focus ? Math.min(this.mapDef.goalY, focus.body.position.y || this.mapDef.goalY) : this.mapDef.goalY;
      targetY = Math.max(0, focusY - visibleH * 0.55);
      this.targetCameraZoom = 1.32;
    } else if (this.finishedMarbles.length > 0 && this.phase === 'running') {
      const dramaT = this.firstGoalTime >= 0
        ? Math.max(0, 1 - (this.gameTime - this.firstGoalTime) / FIRST_GOAL_DRAMA_MS)
        : 0;
      targetY = Math.max(0, this.mapDef.goalY - visibleH * (0.52 + dramaT * 0.08));
      this.targetCameraZoom = 1.42 + dramaT * 0.88;
    } else if (active.length > 0 && this.phase !== 'idle') {
      const ordered = [...active].sort((a, b) => b.body.position.y - a.body.position.y);
      const targetIndex = this.config.mode === 'single' ? 0 : 0;
      const tracked = ordered[targetIndex] ?? ordered[0];
      targetY = Math.max(0, tracked.body.position.y - visibleH * 0.42);

      const lead = ordered[0];
      const second = ordered[1];
      const closeRace = Boolean(second) && Math.abs(lead.body.position.y - second.body.position.y) < 130;
      const nearFinish = this.mapDef.goalY - lead.body.position.y < 520;
      this.targetCameraZoom = nearFinish ? 1.72 : closeRace ? 1.34 : 1;
    } else if (this.phase === 'running' && active.length === 0) {
      // All marbles finished — hold camera at the finish line area
      targetY = Math.max(0, this.mapDef.goalY - visibleH * 0.6);
      this.targetCameraZoom = 1.3;
    } else {
      this.targetCameraZoom = 1;
    }
    const maxY = Math.max(0, this.mapDef.worldHeight - visibleH);
    targetY = Math.min(targetY, maxY);
    const t = Math.min(1, deltaMs / 220);
    this.cameraZoom += (this.targetCameraZoom - this.cameraZoom) * Math.min(1, deltaMs / 260);
    this.updateDrawOffset(rect.width);
    this.cameraY += (targetY - this.cameraY) * t;
  }

  private getEffectiveScale() {
    return this.scale * this.cameraZoom;
  }

  private updateDrawOffset(canvasWidth: number) {
    this.offsetX = (canvasWidth - this.mapDef.worldWidth * this.getEffectiveScale()) / 2;
  }

  handlePointerMove(clientX: number, clientY: number) {
    const rect = this.canvas.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const miniW = 84;
    const miniH = Math.min(rect.height - 34, 360);
    const x = 14;
    const y = 14;
    if (mx < x || mx > x + miniW || my < y || my > y + miniH || miniH < 140) {
      this.hoverCameraY = null;
      this.canvas.style.cursor = '';
      return;
    }
    const sy = miniH / this.mapDef.worldHeight;
    const visibleH = rect.height / Math.max(this.getEffectiveScale(), 0.001);
    this.hoverCameraY = (my - y) / sy - visibleH / 2;
    this.canvas.style.cursor = 'ns-resize';
  }

  clearPointerHover() {
    this.hoverCameraY = null;
    this.canvas.style.cursor = '';
  }

  private drawMarbles(ctx: CanvasRenderingContext2D, ts: number) {
    const radius = this.marbleRadius(this.config.students.length);

    // Draw finished marbles at goal line (fading out)
    for (const m of this.finishedMarbles) {
      const elapsed = this.gameTime - m.finishTime;
      m.opacity = Math.max(0, 1 - elapsed / 2500);
      if (m.opacity <= 0) continue;
      const goalX = (m.rank - 1) * (radius * 2.5) + 30;
      const goalY = this.mapDef.goalY + 25;
      this.drawOneMarble(ctx, goalX, goalY, radius, m, m.rank, m.opacity, ts);
    }

    // Draw active marbles
    for (const m of this.marbles) {
      if (m.finished) continue;
      const { x, y } = m.body.position;
      this.drawOneMarble(ctx, x, y, radius, m, 0, 1, ts);
    }
  }

  private drawOneMarble(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, radius: number,
    m: MarbleData, rank: number, opacity: number, _ts: number,
  ) {
    ctx.save();
    ctx.globalAlpha = opacity;

    if (rank === 1) {
      ctx.shadowColor = '#FBBF24';
      ctx.shadowBlur = 24;
    } else if (rank > 0) {
      ctx.shadowColor = m.color;
      ctx.shadowBlur = 12;
    }

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);

    const grd = ctx.createRadialGradient(
      x - radius * 0.32, y - radius * 0.32, radius * 0.05,
      x, y, radius,
    );
    grd.addColorStop(0, `hsl(${m.hue}, 60%, 92%)`);
    grd.addColorStop(0.4, m.color);
    grd.addColorStop(1, `hsl(${m.hue}, 65%, 28%)`);
    ctx.fillStyle = grd;
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, radius - 0.5, 0, Math.PI * 2);
    ctx.clip();
    ctx.translate(x, y);
    ctx.rotate(m.spin);
    ctx.strokeStyle = `hsla(${m.hue + 42}, 90%, 92%, 0.62)`;
    ctx.lineWidth = Math.max(2, radius * 0.18);
    for (let sx = -radius * 2; sx <= radius * 2; sx += radius * 0.72) {
      ctx.beginPath();
      ctx.moveTo(sx, -radius * 1.4);
      ctx.lineTo(sx + radius * 0.8, radius * 1.4);
      ctx.stroke();
    }
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    ctx.beginPath();
    ctx.ellipse(-radius * 0.34, -radius * 0.38, radius * 0.22, radius * 0.12, -0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = rank === 1 ? '#FBBF24' : 'rgba(255,255,255,0.22)';
    ctx.lineWidth = rank === 1 ? 2.5 : 1;
    ctx.stroke();

    // Shield ring
    if (m.shielded) {
      const shieldPulse = Math.sin(Date.now() * 0.006) * 0.2 + 0.8;
      ctx.beginPath();
      ctx.arc(x, y, radius + 5, 0, Math.PI * 2);
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = `rgba(96,165,250,${shieldPulse})`;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = '#60A5FA';
      ctx.shadowBlur = 10;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
    }

    // Name label
    ctx.shadowBlur = 0;
    const name = m.student.name;
    if (radius >= 14) {
      const fontSize = Math.min(radius * 0.72, 13);
      ctx.font = `bold ${fontSize}px Pretendard, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 3;
      const label = name.length > 4 ? name.slice(0, 4) : name;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(m.spin * 0.18);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    } else {
      const fontSize = Math.max(8, radius * 0.7);
      ctx.font = `bold ${fontSize}px Pretendard, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 3;
      const label = name.length > 3 ? name.slice(0, 3) : name;
      ctx.fillText(label, x, y + radius + 2);
    }

    // Rank badge
    if (rank > 0) {
      const br = Math.max(7, radius * 0.5);
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(x + radius * 0.65, y - radius * 0.65, br, 0, Math.PI * 2);
      ctx.fillStyle = rank === 1 ? '#FBBF24' : rank === 2 ? '#94A3B8' : rank === 3 ? '#CD7F32' : '#475569';
      ctx.fill();
      ctx.font = `bold ${Math.max(8, br)}px Pretendard, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = rank <= 3 ? '#1E293B' : 'white';
      ctx.fillText(String(rank), x + radius * 0.65, y - radius * 0.65);
    }

    ctx.restore();
  }

  private drawCountdown(ctx: CanvasRenderingContext2D, W: number, H: number) {
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, W, H);

    const text = this.countdownValue > 0 ? String(this.countdownValue) : 'GO!';
    const progress = 1 - this.countdownTimer / 1000;
    const size = 140 + (1 - progress) * 40;
    ctx.font = `bold ${size}px Pretendard, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = Math.max(0.2, progress);
    ctx.shadowColor = '#5EEAD4';
    ctx.shadowBlur = 40;
    ctx.fillStyle = this.countdownValue === 0 ? '#FBBF24' : '#5EEAD4';
    ctx.fillText(text, W / 2, H / 2);
    ctx.restore();
  }

  private drawStartPrompt(ctx: CanvasRenderingContext2D, W: number, H: number) {
    ctx.save();
    const alpha = Math.sin(Date.now() * 0.002) * 0.3 + 0.7;
    ctx.globalAlpha = alpha;
    ctx.font = 'bold 22px Pretendard, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#5EEAD4';
    ctx.shadowColor = '#5EEAD4';
    ctx.shadowBlur = 12;
    ctx.fillText('Space 또는 클릭으로 시작', W / 2, H - 36);
    ctx.restore();
  }

  /** Called by GameScreen to render live rank board into a separate element */
  getLiveRanks(): Array<{ rank: number; name: string; color: string }> {
    return this.finishedMarbles.slice(0, 10).map((m) => ({
      rank: m.rank,
      name: m.student.name,
      color: m.color,
    }));
  }

  getLiveOrder(limit = 15): Array<{ rank: number; name: string; color: string; finished: boolean }> {
    const finished = this.finishedMarbles.map((m) => ({
      rank: m.rank,
      name: m.student.name,
      color: m.color,
      finished: true,
    }));
    const active = this.marbles
      .filter((m) => !m.finished)
      .sort((a, b) => b.body.position.y - a.body.position.y)
      .map((m, i) => ({
        rank: this.rankCounter + i + 1,
        name: m.student.name,
        color: m.color,
        finished: false,
      }));
    return [...finished, ...active].slice(0, limit);
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.resizeObserver.disconnect();
    Matter.World.clear(this.engine.world, false);
    Matter.Engine.clear(this.engine);
    this.audio?.destroy();
  }
}
